import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

/**
 * OnboardingWizard — shown once to new team admins.
 * Props:
 *   token        – JWT for API calls
 *   onDismiss()  – called when wizard is dismissed (permanently)
 */
export default function OnboardingWizard({ token, onDismiss }) {
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState({ has_event: false, has_driver: false, has_synced: false });
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    axios.get('/api/auth/onboarding-status', { headers })
      .then(r => setStatus(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allDone = (status.has_event && status.has_driver && syncDone) || dismissing;

  async function dismiss() {
    setDismissing(true);
    try { await axios.post('/api/auth/complete-onboarding', {}, { headers }); } catch (_) {}
    onDismiss();
  }

  async function handleSync() {
    setSyncRunning(true);
    try {
      await axios.post('/api/admin/radnet-sync', {}, { headers });
      setSyncDone(true);
    } catch (_) {
      setSyncDone(true); // treat as done even on error — not a blocker
    } finally {
      setSyncRunning(false);
    }
  }

  const Step = ({ done, num, title, desc, action }) => (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 16, padding: '18px 20px',
      background: done ? 'rgba(var(--brand-secondary-rgb),0.04)' : 'rgba(17,19,24,0.6)',
      border: `1px solid ${done ? 'rgba(var(--brand-secondary-rgb),0.25)' : 'rgba(59,73,75,0.4)'}`,
      borderRadius: 2,
    }}>
      {/* Checkmark / number */}
      <div style={{
        width: 28, height: 28, flexShrink: 0, borderRadius: '50%',
        border: `2px solid ${done ? 'var(--brand-secondary)' : 'rgba(59,73,75,0.6)'}`,
        background: done ? 'rgba(var(--brand-secondary-rgb),0.15)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700,
        color: done ? 'var(--brand-secondary)' : '#849495',
        transition: 'all 0.2s',
      }}>
        {done ? '✓' : num}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 800,
          fontStyle: 'italic', color: done ? 'var(--brand-secondary)' : '#e2e2e8',
          textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4,
        }}>{title}</div>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
          color: '#849495', letterSpacing: '0.05em', lineHeight: 1.5,
        }}>{desc}</div>
      </div>

      {/* Action */}
      {!done && action && (
        <div style={{ flexShrink: 0 }}>
          {action}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1200,
      background: 'rgba(10,12,16,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: '100%', maxWidth: 540,
        background: 'rgba(17,19,24,0.98)',
        border: '1px solid rgba(var(--brand-primary-rgb),0.25)',
        padding: '32px 28px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
            letterSpacing: '0.18em', color: 'var(--brand-primary)', marginBottom: 8,
          }}>SETUP · 3 SCHRITTE</div>
          <div style={{
            fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 900,
            fontStyle: 'italic', color: '#e2e2e8', textTransform: 'uppercase',
            lineHeight: 1.1,
          }}>Willkommen bei<br />Crit Roster 🚴</div>
          <div style={{
            marginTop: 10, fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
            color: '#849495', letterSpacing: '0.04em', lineHeight: 1.5,
          }}>
            In 3 Schritten bist du startklar. Kein Schritt ist Pflicht — überspringe was du später machen willst.
          </div>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          <Step
            done={status.has_event}
            num="1"
            title="Erstes Event anlegen"
            desc="Trag dein nächstes Rennen ein — Datum, Ort, Anmeldeschluss."
            action={
              <button onClick={() => navigate('/events')} style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
                letterSpacing: '0.12em', background: 'var(--brand-primary)', color: '#002022',
                border: 'none', padding: '8px 14px', cursor: 'pointer', textTransform: 'uppercase',
              }}>
                → EVENTS
              </button>
            }
          />
          <Step
            done={status.has_driver}
            num="2"
            title="Fahrer einladen"
            desc="Teile den Einladungslink im Team-Chat oder lade Fahrer direkt ein."
            action={
              <button onClick={() => navigate('/drivers')} style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
                letterSpacing: '0.12em', background: 'var(--brand-primary)', color: '#002022',
                border: 'none', padding: '8px 14px', cursor: 'pointer', textTransform: 'uppercase',
              }}>
                → FAHRER
              </button>
            }
          />
          <Step
            done={syncDone}
            num="3"
            title="Rad-net Sync starten"
            desc="Importiert automatisch Punkte und Ränge aus der BDR-Rangliste."
            action={
              <button
                onClick={handleSync}
                disabled={syncRunning}
                style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.12em',
                  background: syncRunning ? 'rgba(var(--brand-primary-rgb),0.3)' : 'var(--brand-primary)',
                  color: '#002022', border: 'none', padding: '8px 14px',
                  cursor: syncRunning ? 'default' : 'pointer', textTransform: 'uppercase',
                }}
              >
                {syncRunning ? '...' : '→ SYNC'}
              </button>
            }
          />
        </div>

        {/* Footer buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={dismiss}
            style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
              letterSpacing: '0.12em', background: 'transparent', color: '#849495',
              border: '1px solid rgba(59,73,75,0.5)', padding: '10px 18px',
              cursor: 'pointer', textTransform: 'uppercase',
            }}
          >
            Überspringen
          </button>
          <button
            onClick={dismiss}
            style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
              letterSpacing: '0.12em',
              background: allDone ? 'var(--brand-secondary)' : 'rgba(var(--brand-secondary-rgb),0.15)',
              color: allDone ? 'var(--accent-contrast)' : 'var(--brand-secondary)',
              border: `1px solid ${allDone ? 'var(--brand-secondary)' : 'rgba(var(--brand-secondary-rgb),0.3)'}`,
              padding: '10px 18px', cursor: 'pointer', textTransform: 'uppercase',
              transition: 'all 0.2s',
            }}
          >
            {allDone ? '✓ Alles erledigt' : 'Fertig'}
          </button>
        </div>
      </div>
    </div>
  );
}
